/* eslint-disable operator-linebreak */
import React from 'react';
import { FormApi } from 'final-form';
import { FormControlLabel, FormControlLabelClassKey } from '@material-ui/core';
import { ClassNameMap } from '@material-ui/core/styles/withStyles';
import TableDatePicker from 'ui/TableInputs/TableDatePicker/TableDatePicker';
import Input, { AmountInput, InputWithSelect, Select, SelectInnerItem, SelectItem, Textarea } from 'ui/Input';
import { MAX_ACCOUNT_LENGTH, ACCOUNT_LENGTHS, TransferFrequencyList } from 'utils/constants';
import { onlyNCharacters, onlyNumbers } from 'utils/formatters';
import Switch from 'ui/Switch/Switch';
import { IAccount } from 'store/accounts.model';
import { IFormValues, ITransferActions, ITransfersState } from './ToAnotherBank.model';

type FormType = FormApi<IFormValues, Partial<IFormValues>>;

const renderSourceAccountField = (
  accounts: IAccount[],
  submitting: boolean,
) => (
  <Select name="sourceAccount" label="Source Account" disabled={submitting}>
    {accounts.map(({ accountNumber }) => (
      <SelectItem value={accountNumber} key={accountNumber}>
        {accountNumber}
      </SelectItem>
    ))}
  </Select>
);

export const renderTransferTypeField = (
  types: ITransfersState['types'],
  isLookingUp: ITransfersState['isLookingUp'],
  findBeneficiaryName: ITransferActions['findBeneficiaryName'],
  form: FormType,
  fetchBeneficiaries: ITransferActions['fetchBeneficiaries'],
) => {
  const handleChange = ({ target }) => {
    form.mutators.changeFieldValue(target?.name, target?.value); // Override default handleChange()
    form.mutators.changeFieldValue('destinationBankCode', undefined);
    findBeneficiaryName(form.getState().values);
    fetchBeneficiaries(form.getState().values);
  };
  return (
    <Select
      name="transferType"
      label="Transfer Type"
      disabled={isLookingUp}
      SelectProps={{
        onChange: handleChange,
      }}
    >
      {types.map(({ name, key }) => (
        <SelectItem value={key} key={key}>
          {name}
        </SelectItem>
      ))}
    </Select>
  );
};

const renderAmountField = (
  submitting: boolean,
  values: IFormValues,
  getTransactionCharge?: ITransferActions['getTransactionCharge'],
) => (
  <AmountInput
    name="amount"
    label="Amount"
    disabled={submitting}
    onKeyUp={(e) => {
      if (!getTransactionCharge) return;
      getTransactionCharge((e.nativeEvent.target as HTMLInputElement)?.value.replace(/[^\d.]/g, ''),
        values.transferType);
    }}
  />
);

export const renderBankField = (
  banks: ITransfersState['banks'],
  isLookingUp: ITransfersState['isLookingUp'],
  actions: ITransferActions,
  form: FormType,
) => {
  const { handleSelectBank, findBeneficiaryName } = actions;

  const handleChange = ({ target }) => {
    // Override default handleChange()
    form.mutators.changeFieldValue(target?.name, target?.value);
    handleSelectBank(target.value);
    findBeneficiaryName(form.getState().values);
  };

  return (
    <Select
      name="destinationBankCode"
      label="Bank"
      disabled={isLookingUp}
      SelectProps={{
        onChange: handleChange,
      }}
    >
      {banks.map(({ name, code }) => (
        <SelectItem value={code} key={code}>
          {name}
        </SelectItem>
      ))}
    </Select>
  );
};

export const renderNonInstantBeneficiaryNameField = (values: IFormValues) => {
  if (values.transferType !== 'NON_INSTANT') return null;

  return <Input name="beneficiaryName" label="Beneficiary Name" />;
};

const renderNarrationField = (submitting: boolean) => (
  <Textarea
    name="narration"
    label="Narration"
    disabled={submitting}
    fieldProps={{
      parse: onlyNCharacters(25),
    }}
  />
);

export const renderAccountNumberField = (
  values: IFormValues,
  { transferBeneficiaries, isLookingUp, lastLookedUpAccount, extras }: ITransfersState,
  findBeneficiaryName: ITransferActions['findBeneficiaryName'],
  form: FormType,
) => {
  const { destinationAccountName } = extras;
  const { destinationAccountNo } = values;

  const fieldName = 'destinationAccountNo';

  const characterLimits = MAX_ACCOUNT_LENGTH;
  const showLoader = Boolean(!destinationAccountName && isLookingUp);

  const showMessage =
    ACCOUNT_LENGTHS.includes(destinationAccountNo?.length) &&
    Boolean(lastLookedUpAccount) &&
    lastLookedUpAccount === values.destinationAccountNo;

  const handleClear = () => {
    form.mutators.changeFieldValue(fieldName, '');
  };

  const handleBlur = () => {
    findBeneficiaryName(values);
  };

  const handleSelect = (event) => {
    const { value: accountNumber } = event.target as HTMLInputElement;
    form.mutators.changeFieldValue(fieldName, accountNumber);

    const beneficiary = transferBeneficiaries.find((ben) => ben.accountNumber === accountNumber);
    if (beneficiary) {
      form.mutators.changeFieldValue('destinationBankCode', beneficiary.bankCode);
      form.mutators.changeFieldValue('beneficiaryName', beneficiary.name);
    }
    findBeneficiaryName(form.getState().values);
  };

  return (
    <>
      <InputWithSelect
        name={fieldName}
        label="Account Number"
        isEmpty={!values.destinationAccountNo}
        fieldProps={{
          parse: (value: string) => onlyNCharacters(characterLimits)(onlyNumbers(value)),
        }}
        disabled={showLoader}
        isLoading={showLoader}
        showMessage={showMessage}
        message={destinationAccountName}
        onClear={handleClear}
        onBlur={handleBlur}
        onSelectItemChange={handleSelect}
      >
        {transferBeneficiaries.map(({ name, accountNumber }) => (
          <SelectItem value={accountNumber} key={accountNumber}>
            <SelectInnerItem label={name} value={accountNumber} />
          </SelectItem>
        ))}
      </InputWithSelect>
    </>
  );
};

export const renderScheduleControl = (
  toggleScheduleTransfer: () => void,
  toggleSchedule: boolean | null,
  classes: Partial<ClassNameMap<FormControlLabelClassKey>> | undefined,
) => (
  <FormControlLabel
    control={<Switch />}
    checked={toggleSchedule === null ? false : toggleSchedule}
    onChange={toggleScheduleTransfer}
    label="Schedule transfer for later"
    labelPlacement="end"
    classes={classes}
    name="scheduleTransfer"
  />
);

export const renderScheduleForm = (
  submitting: boolean,
  dateFilterChange: (name: string, value?: string | null | undefined) => void,
  handleClearDateInput: (name: string, value?: string | null | undefined) => void,
  values: IFormValues,
) => (
  <>
    <Select name="frequency" label="Frequency" disabled={submitting}>
      {TransferFrequencyList.map(({ label, value }) => (
        <SelectItem value={value} key={value}>
          {label}
        </SelectItem>
      ))}
    </Select>
    <TableDatePicker
      name="specialDate"
      label="Special Date"
      onChange={dateFilterChange}
      onClickClear={handleClearDateInput}
      disabled={submitting || values.frequency !== 'specialDate'}
    />
  </>
);

// Export Fields

export const renderTransferFieldsFrom = (
  values: IFormValues,
  accounts: IAccount[],
  state: ITransfersState,
  actions: ITransferActions,
  form: FormApi<IFormValues, Partial<IFormValues>>,
  submitting: boolean,
) => (
  <>
    {renderSourceAccountField(accounts, submitting)}
    {renderTransferTypeField(state.types, state.isLookingUp,
      actions.findBeneficiaryName, form, actions.fetchBeneficiaries)}
    {renderAmountField(submitting, values, actions.getTransactionCharge)}
  </>
);

export const renderTransferFieldsTo = (
  values: IFormValues,
  accounts: IAccount[],
  state: ITransfersState,
  actions: ITransferActions,
  form: FormApi<IFormValues, Partial<IFormValues>>,
  submitting: boolean,
  toggleSchedule: boolean | null,
  toggleScheduleTransfer: () => void,
  classes: Partial<ClassNameMap<FormControlLabelClassKey>> | ClassNameMap<'label' | 'root'> | undefined,
  dateFilterChange: (name: string, value?: string | null | undefined) => void,
  handleClearDateInput: (name: string, value?: string | null | undefined) => void,
) => (
  <>
    {renderBankField(state.banks, state.isLookingUp, actions, form)}
    {renderAccountNumberField(values, state, actions.findBeneficiaryName, form)}
    {renderNonInstantBeneficiaryNameField(values)}
    {renderNarrationField(submitting)}
    {renderScheduleControl(toggleScheduleTransfer, toggleSchedule, classes)}
    {values.scheduleTransfer && renderScheduleForm(submitting, dateFilterChange, handleClearDateInput, values)}
  </>
);
